import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import {
  customizationBuyerName,
  formatCustomizationMoney,
} from '../../api/customizationsAPI';
import BoutiqueBriefView from '../../components/customizations/BoutiqueBriefView';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { negotiationsAPI } from '../../api/negotiationsAPI';
import { getOfferingVertical } from './offeringCustomizationVertical';

const Page = styled.main`max-width: 900px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Back = styled(Link)`color: #0f3d36; display: inline-block; font-weight: 700; margin-bottom: 1.25rem;`;
const Heading = styled.div`align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; margin-bottom: 1rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0;`;
const Buyer = styled.p`color: var(--text-secondary); font-weight: 600; margin: 0.35rem 0 0;`;
const Badge = styled.span`
  background: #e7f4ef; border-radius: 999px; color: #0f3d36; font-size: 0.8rem;
  font-weight: 800; padding: 0.45rem 0.75rem; text-transform: capitalize;
`;
const Notice = styled.div`
  background: #fff7d6; border-radius: 0.6rem; color: #7c5b00;
  font-weight: 600; margin-bottom: 1.25rem; padding: 0.85rem 1rem;
`;
const SectionTitle = styled.h2`color: var(--text-primary); font-size: 1.25rem; margin: 1.6rem 0 0.75rem;`;
const Card = styled.section`
  background: var(--bg-primary, #fff); border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.75rem; margin-top: 1rem; padding: 1.1rem;
`;
const QuotePrice = styled.div`color: #0f3d36; font-size: 1.6rem; font-weight: 900;`;
const Muted = styled.div`color: var(--text-secondary); font-size: 0.85rem; margin-top: 0.3rem;`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1.25rem;`;
const Button = styled.button`
  border: 0; border-radius: 0.55rem; cursor: pointer; font-weight: 800; padding: 0.8rem 1.1rem;
  &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const PrimaryLink = styled(Link)`
  background: #0f3d36; border-radius: 0.55rem; color: #fff; font-weight: 800;
  padding: 0.8rem 1.1rem; text-decoration: none;
`;
const OutlineLink = styled(Link)`
  border: 1.5px solid #0f3d36; border-radius: 0.55rem; color: #0f3d36;
  font-weight: 800; padding: 0.72rem 1.1rem; text-decoration: none;
`;
const Danger = styled(Button)`background: #fff; border: 1.5px solid #b91c1c; color: #b91c1c;`;
const State = styled.div`padding: 4rem 1rem; text-align: center;`;

export function OfferingCustomizationDetailPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [declining, setDeclining] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setRequest(await vertical.getRequest(requestId));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [requestId, vertical]);

  useEffect(() => { load(); }, [load]);

  const decline = async () => {
    if (!window.confirm(
      `Decline this request?\n\nThe buyer can choose another ${vertical.label}. This can’t be undone.`,
    )) return;
    setDeclining(true);
    try {
      await vertical.declineRequest(requestId);
      toast.success('Request declined.');
      navigate(vertical.ownerInboxPath, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong. Try again.');
      load();
    } finally {
      setDeclining(false);
    }
  };

  if (loading && !request) return <LoadingSpinner text="Loading custom order…" />;
  if (error || !request) {
    return (
      <Page>
        <State>
          <h2>This request isn’t available.</h2>
          <Back to={vertical.ownerInboxPath}>Back to requests</Back>
        </State>
      </Page>
    );
  }

  const quote = request.active_quote || request.quotes?.[0];
  const canQuote = ['sent', 'quoted'].includes(request.state);
  const chatPath = request.negotiation_id
    ? `/negotiations/${negotiationsAPI.buildNegotiationRef(request.negotiation_id)}`
    : null;

  return (
    <Page>
      <Back to={vertical.ownerInboxPath}>← Back to requests</Back>
      <Heading>
        <div>
          <Title>Custom order request</Title>
          <Buyer>{customizationBuyerName(request)}</Buyer>
        </div>
        <Badge>{request.state.replace(/_/g, ' ')}</Badge>
      </Heading>
      {request.expires_at && ['sent', 'quoted'].includes(request.state) ? (
        <Notice>
          {request.state === 'quoted' ? 'This quote' : 'This request'} expires on{' '}
          {new Date(request.expires_at).toLocaleDateString()}.
        </Notice>
      ) : null}

      <SectionTitle>Order brief</SectionTitle>
      <Card>
        <BoutiqueBriefView
          brief={request.boutique_brief}
          offering={request.offering}
          references={request.references}
        />
        <Muted>
          Budget {formatCustomizationMoney(request.budget_min_cents)}
          {' – '}
          {formatCustomizationMoney(request.budget_max_cents)}
        </Muted>
        {request.special_requests ? <Muted>Notes: {request.special_requests}</Muted> : null}
      </Card>

      <Card>
        <SectionTitle style={{ marginTop: 0 }}>Conversation</SectionTitle>
        {chatPath ? <OutlineLink to={chatPath}>Ask a question</OutlineLink> : <Muted>Chat unavailable</Muted>}
      </Card>

      {quote ? (
        <Card>
          <SectionTitle style={{ marginTop: 0 }}>Quote</SectionTitle>
          <QuotePrice>{formatCustomizationMoney(quote.amount_cents)}</QuotePrice>
          <Muted>{quote.delivery_days} day delivery</Muted>
          {quote.message ? <Muted>{quote.message}</Muted> : null}
        </Card>
      ) : null}

      {canQuote ? (
        <Actions>
          <PrimaryLink to={vertical.ownerQuotePath(request.id)}>
            {request.state === 'quoted' ? 'Revise quote' : 'Send quote'}
          </PrimaryLink>
          {request.state === 'sent' ? (
            <Danger type="button" disabled={declining} onClick={decline}>
              {declining ? 'Declining…' : 'Decline request'}
            </Danger>
          ) : null}
        </Actions>
      ) : null}
    </Page>
  );
}

const BoutiqueCustomizationDetailPage = () => <OfferingCustomizationDetailPage verticalKey="boutique" />;
export const DesignerCustomizationDetailPage = () => <OfferingCustomizationDetailPage verticalKey="designer" />;
export default BoutiqueCustomizationDetailPage;
